import { withKey } from "@tellescope/ui";
import type { FormSurface } from "../store";
import { FormResponseDialogWizard } from "./FormResponseDialogWizard";
import { PinnedFormDialogWizard } from "./PinnedFormDialogWizard";
import { FormLinkWizard } from "./FormLinkWizard";
import type { SurfaceWizardProps } from "./types";

export interface EnduserFormWizardProps extends SurfaceWizardProps {
  /**
   * Which surface is rendering the form. Each has its own store, so two forms open at once cannot
   * overwrite each other, and each persists differently — the deeplink keeps its state in the URL
   * while the in-app dialogs stay in memory.
   */
  surface: FormSurface;
}

/**
 * Renders the enduser form wizard for a given surface.
 *
 * The store cannot be chosen inside the wizard body, since selecting a hook at runtime would mean
 * calling hooks conditionally. Each surface instead has its own component that calls its store hook
 * unconditionally and hands it to the shared body.
 */
const EnduserFormWizardInternal = ({ surface, ...props }: EnduserFormWizardProps) => {
  switch (surface) {
    case "form-response-dialog":
      return <FormResponseDialogWizard {...props} />;
    case "pinned-form-dialog":
      return <PinnedFormDialogWizard {...props} />;
    case "form-link":
      return <FormLinkWizard {...props} />;
  }
};

// Keyed by form/response identity so a caller re-rendering this component with a different form or
// response (e.g. chaining from one form into another without unmounting the dialog around it) always
// gets a fresh instance — the navigation hook's internal field-stack/traversal state must never carry
// over from one form to a different one. responseId is the more specific identity once a response
// exists; formId is the only one available before a response has been prepared.
export const EnduserFormWizard = withKey<EnduserFormWizardProps>(
  EnduserFormWizardInternal,
  (props) => props.formArgs.responseId ?? props.formArgs.formId
);
